/**
 * Admin appointments queue: /admin/appointments
 *
 * Combines pending web bookings + walk-ins recorded by staff into one
 * chronological triage list. Filters mirror the Inspections list so ops
 * feels the same everywhere.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarCheck2, Loader2, Plus, Phone, MessageCircle, ChevronLeft, ChevronRight,
  CircleCheck, CircleX, ClipboardCheck, RotateCcw, Store, Globe2, BellRing, ChevronDown, ChevronUp,
  Send, UserCheck, Truck, CalendarClock, Wrench, Printer,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { packagesText, pickupText, shareAptCard } from '../../../lib/aptMessages';
import { RescheduleDialog } from '../../../components/admin/appointments/RescheduleDialog';
import { WeekView } from '../../../components/admin/appointments/WeekView';
import { SlotAlertCard } from '../../../components/admin/appointments/SlotAlertCard';
import { fmtINR, formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';
import PpfHeatmap from './PpfHeatmap';
import SkillBadge from '../../../components/admin/SkillBadge';

const STATUS_CLS = {
  pending:   'bg-amber-100 text-amber-800 border-amber-200',
  confirmed: 'bg-sky-100 text-sky-800 border-sky-200',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
  no_show:   'bg-rose-100 text-rose-800 border-rose-200',
};

const SOURCE_ICON = {
  website: Globe2,
  walk_in: Store,
  phone: Phone,
  whatsapp: MessageCircle,
  referral: RotateCcw,
};

const fmtSlot = (s) => s || '';

const todayIso = () => new Date().toLocaleDateString('en-CA');

const TABS = [
  ['pending', 'Requested', 'pending'], ['confirmed', 'Confirmed', 'confirmed'], ['today', 'Today', 'today'],
  ['completed', 'Completed', 'completed'], ['cancelled', 'Cancelled', 'cancelled'], ['all', 'All', null],
];

const ExtraBadges = ({ r }) => (
  <>
    {r.pickup?.required && (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-800 border border-violet-200" title={pickupText(r.pickup)} data-testid={`apt-pickup-badge-${r.id}`}>
        <Truck size={10} /> {r.pickup.staff_id ? 'Pickup' : 'Pickup requested'}{r.pickup.time ? ` ${r.pickup.time}` : ''}
      </span>
    )}
    {(r.packages || []).length > 0 && (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200" title={packagesText(r)} data-testid={`apt-packages-badge-${r.id}`}>
        {r.packages.length} pkg · ₹ {fmtINR(r.packages_total || 0)}
      </span>
    )}
    {r.reschedule_count > 0 && <span className="text-[10px] font-semibold text-slate-500" data-testid={`apt-rescheduled-${r.id}`}>Rescheduled</span>}
  </>
);

const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${STATUS_CLS[status] || STATUS_CLS.pending}`}
    data-testid={`apt-status-${status}`}>{status.replace('_', ' ')}</span>
);

/** Single reminder bucket rendered inside the amber panel. */
const ReminderGroup = ({ title, emptyText, items, kind, onSend, busy }) => (
  <div className="bg-white border border-amber-200 rounded-lg overflow-hidden" data-testid={`apt-reminder-group-${kind}`}>
    <div className="px-3 py-2 border-b border-amber-100 flex items-center justify-between">
      <div className="text-xs font-bold text-amber-900 uppercase tracking-wide">{title}</div>
      <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{items.length}</span>
    </div>
    {items.length === 0 ? (
      <div className="px-3 py-4 text-xs text-slate-400 text-center">{emptyText}</div>
    ) : (
      <ul className="divide-y divide-amber-100">
        {items.map((r) => {
          const c = r.customer || {}; const v = r.vehicle || {};
          const slot = (r.slot_window || '').replace(/:00-/, '–').replace(/:00$/, '');
          const vehicle = [v.year, v.make, v.model].filter(Boolean).join(' ') || v.registration_number || '—';
          return (
            <li key={r.id} className="px-3 py-2 flex items-center gap-2" data-testid={`apt-reminder-${kind}-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] font-semibold text-slate-800">{r.ref_no}</span>
                  <span className="text-[10px] font-semibold text-slate-500">{slot}</span>
                </div>
                <div className="text-xs text-slate-800 font-semibold truncate">{c.name || '—'} · <span className="font-mono text-slate-500 text-[11px]">{c.phone || '—'}</span></div>
                <div className="text-[11px] text-slate-500 truncate">{r.service_type} · {vehicle}</div>
              </div>
              <button
                onClick={() => onSend(r, kind)}
                disabled={!c.phone || busy[r.id]}
                className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-[11px] font-semibold px-2.5 py-1.5 rounded"
                data-testid={`apt-reminder-send-${kind}-${r.id}`}
                title="Share the reminder card + message on WhatsApp, and mark it as sent"
              >
                {busy[r.id] ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
                Send
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const AppointmentsPage = () => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const LIMIT = 25;
  const [meta, setMeta] = useState({ statuses: [], services: [] });
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState('');
  const [rescheduling, setRescheduling] = useState(null);
  const [view, setViewState] = useState(() => localStorage.getItem('apt-view') || 'list');
  const setView = (v) => { localStorage.setItem('apt-view', v); setViewState(v); };
  const [filters, setFilters] = useState({
    status: 'pending', service_type: 'all', date_from: '', date_to: '', q: '',
  });
  // Reminders — cached from /reminders/due; refreshed alongside the main
  // list load so the panel stays consistent with the queue underneath.
  const [reminders, setReminders] = useState({ next_day: [], today_soon: [], counts: { next_day: 0, today_soon: 0 } });
  const [remindersOpen, setRemindersOpen] = useState(true);
  const [reminderBusy, setReminderBusy] = useState({});
  const [digestBusy, setDigestBusy] = useState(false);

  // Manually fire the ops-inbox digest — same call the 09:30 IST scheduler
  // makes. Surfaces the SMTP error inline (via toast alert) so ops can
  // diagnose right in the UI without opening logs.
  const sendDigestNow = async () => {
    setDigestBusy(true);
    try {
      const { data } = await axios.post(`${API}/appointments/stats/send-digest`, {}, { headers: authHeader() });
      if (data?.sent) {
        alert(`Digest sent to ops inbox.\n\nPending: ${data.pending}\nToday: ${data.today}\nTomorrow: ${data.tomorrow}`);
      } else if (data?.skipped) {
        alert(`Digest skipped: ${data.reason || 'queue empty'}.\nNothing to send right now.`);
      } else {
        alert(`Digest not sent. Reason: ${data?.error || 'unknown'}`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'unknown';
      alert(`Digest failed (HTTP ${status || '?'}): ${detail}\n\nCommon fixes:\n1. Make sure you're logged in as the master admin\n2. Confirm SMTP_PASSWORD is set on production and the pod has restarted\n3. Check /var/log/supervisor/backend.err.log on the pod`);
    } finally { setDigestBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const p = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (filters.status === 'today') { p.set('status', 'active'); p.set('date_from', todayIso()); p.set('date_to', todayIso()); }
      else if (filters.status !== 'all') p.set('status', filters.status);
      if (['cancelled', 'completed', 'all'].includes(filters.status)) p.set('order', 'desc');
      if (filters.service_type !== 'all') p.set('service_type', filters.service_type);
      if (filters.date_from && filters.status !== 'today') p.set('date_from', filters.date_from);
      if (filters.date_to && filters.status !== 'today') p.set('date_to', filters.date_to);
      if (filters.q.trim()) p.set('q', filters.q.trim());
      const [list, sum, rem] = await Promise.all([
        axios.get(`${API}/appointments?${p}`, { headers: authHeader() }),
        axios.get(`${API}/appointments/stats/summary`, { headers: authHeader() }),
        axios.get(`${API}/appointments/reminders/due`, { headers: authHeader() }),
      ]);
      setRows(list.data.rows || []);
      setTotal(list.data.total || 0);
      setSummary(sum.data || {});
      setReminders(rem.data || { next_day: [], today_soon: [], counts: { next_day: 0, today_soon: 0 } });
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, page, filters]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/appointments/meta`, { headers: authHeader() });
        setMeta(data || {});
      } catch (e) { /* noop — filters just stay empty */ }
    })();
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };

  const patch = async (id, updates) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await axios.patch(`${API}/appointments/${id}`, updates, { headers: authHeader() });
      await load();
      return true;
    } catch (e) { notifyErr(e); return false; }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  // Confirm, then open the share dialog (its button is the tap WhatsApp needs).
  const confirmAndNotify = async (row) => {
    const ok = await patch(row.id, { status: 'confirmed' });
    if (ok && row.customer?.phone) shareAptCard({ ...row, status: 'confirmed' }, authHeader);
  };

  // Reminder: share the branded card + message, then stamp the "sent" flag.
  const sendReminder = (row, kind) => {
    if (row.customer?.phone) shareAptCard(row, authHeader, kind === '2h' ? 'reminder_2h' : 'reminder_24h');
    setReminderBusy((b) => ({ ...b, [row.id]: true }));
    axios.post(`${API}/appointments/${row.id}/mark-reminder-sent?kind=${kind}`, {}, { headers: authHeader() })
      .then(() => load())
      .catch((e) => notifyErr(e))
      .finally(() => setReminderBusy((b) => ({ ...b, [row.id]: false })));
  };

  const convert = async (row) => {
    if (row.linked_inspection_id) { navigate(`/admin/inspections/${row.linked_inspection_id}`); return; }
    if (!window.confirm(`Create a service inspection from ${row.ref_no}?`)) return;
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      const { data } = await axios.post(`${API}/appointments/${row.id}/convert-to-inspection`, {}, { headers: authHeader() });
      navigate(`/admin/inspections/${data.inspection_id}`);
    } catch (e) { notifyErr(e); setBusy((b) => ({ ...b, [row.id]: false })); }
  };

  const toJobCard = async (row) => {
    if (row.linked_job_card_id) { navigate(`/admin/job-cards/${row.linked_job_card_id}`); return; }
    if (!window.confirm(`Create a job card from ${row.ref_no}?`)) return;
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      const { data } = await axios.post(`${API}/appointments/${row.id}/convert-to-job-card`, {}, { headers: authHeader() });
      navigate(`/admin/job-cards/${data.job_card_id}`);
    } catch (e) { notifyErr(e); setBusy((b) => ({ ...b, [row.id]: false })); }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const stats = useMemo(() => ([
    { key: 'today',     label: 'Today',     value: summary.today ?? 0,     color: 'text-teal-700' },
    { key: 'upcoming',  label: 'Upcoming',  value: summary.upcoming ?? 0,  color: 'text-sky-700' },
    { key: 'pending',   label: 'Pending',   value: summary.pending ?? 0,   color: 'text-amber-700' },
    { key: 'confirmed', label: 'Confirmed', value: summary.confirmed ?? 0, color: 'text-sky-700' },
    { key: 'completed', label: 'Completed', value: summary.completed ?? 0, color: 'text-emerald-700' },
    { key: 'no_show',   label: 'No-show',   value: summary.no_show ?? 0,   color: 'text-rose-700' },
  ]), [summary]);

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-slate-50 min-h-full" data-testid="admin-appointments">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center">
            <CalendarCheck2 size={20} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Appointments</h1>
            <p className="text-xs text-slate-500">Web bookings + walk-in registrations, all in one queue.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5" data-testid="apt-view-toggle">
            {[['list', 'List'], ['week', 'Week']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setView(k)} data-testid={`apt-view-${k}`}
                className={`rounded-md px-4 py-1.5 text-xs font-semibold transition-colors ${view === k ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => navigate('/admin/pickups')} className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold px-3 py-2 rounded-lg" data-testid="apt-pickups-btn">
            <Truck size={14} /> Pickups today
          </button>
          <button
            onClick={sendDigestNow}
            disabled={digestBusy}
            className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg transition"
            data-testid="apt-send-digest-btn"
            title="Manually send the daily appointments digest email now (also runs every day at 09:30 IST)"
          >
            {digestBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            <span className="hidden sm:inline">Send digest</span>
          </button>
          <Link
            to="/admin/appointments/today-cards"
            className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg transition"
            data-testid="apt-today-cards-btn"
            title="Open or print every card for today's appointments"
          ><Printer size={14} /> <span className="hidden sm:inline">Today's cards</span></Link>
          <Link
            to="/admin/appointments/new"
            className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-3 py-2 rounded-lg transition"
            data-testid="apt-new-btn"
          ><Plus size={14} /> <span className="hidden sm:inline">New Appt</span></Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {stats.map((s) => (
          <div key={s.key} className="bg-white border border-slate-200 rounded-xl px-3 py-2" data-testid={`apt-stat-${s.key}`}>
            <div className="text-[10px] uppercase font-semibold tracking-wide text-slate-400">{s.label}</div>
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* PPF-slot heatmap — spot bay backlog before customers do. */}
      <PpfHeatmap
        onPickDate={(d) => {
          setPage(1);
          // Filter the queue to that day; leave service_type alone so
          // both `PPF Package` and `Color PPF Application` rows show up.
          setFilters((f) => ({ ...f, date_from: d, date_to: d }));
        }}
      />

      {/* Reminders due panel — collapses when nothing pending. */}
      {(reminders.counts?.next_day > 0 || reminders.counts?.today_soon > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden" data-testid="apt-reminders-panel">
          <button
            onClick={() => setRemindersOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-100/60 transition"
            data-testid="apt-reminders-toggle"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center">
                <BellRing size={16} />
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-amber-900">
                  {reminders.counts.next_day + reminders.counts.today_soon} reminder{reminders.counts.next_day + reminders.counts.today_soon === 1 ? '' : 's'} due
                </div>
                <div className="text-[11px] text-amber-700">
                  {reminders.counts.today_soon} for the next few hours · {reminders.counts.next_day} for tomorrow
                </div>
              </div>
            </div>
            {remindersOpen ? <ChevronUp size={16} className="text-amber-700" /> : <ChevronDown size={16} className="text-amber-700" />}
          </button>
          {remindersOpen && (
            <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ReminderGroup
                title="Next couple of hours"
                emptyText="No 2-hour reminders due."
                items={reminders.today_soon}
                kind="2h"
                onSend={sendReminder}
                busy={reminderBusy}
              />
              <ReminderGroup
                title="Tomorrow"
                emptyText="No 24-hour reminders due."
                items={reminders.next_day}
                kind="24h"
                onSend={sendReminder}
                busy={reminderBusy}
              />
            </div>
          )}
        </div>
      )}

      <SlotAlertCard authHeader={authHeader} />

      {view === 'week' ? <WeekView authHeader={authHeader} meta={meta} /> : (<>
      <div className="flex flex-wrap gap-1.5" role="tablist" data-testid="apt-tabs">
        {TABS.map(([k, label, countKey]) => (
          <button key={k} type="button" role="tab" aria-selected={filters.status === k} onClick={() => setFilter('status', k)} data-testid={`apt-tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${filters.status === k ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'}`}>
            {label}
            {countKey && <span className={`rounded-full px-1.5 text-[10px] ${filters.status === k ? 'bg-white/20' : 'bg-slate-100'}`} data-testid={`apt-tab-count-${k}`}>{summary[countKey] ?? 0}</span>}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <input
          className={`${inputCls} flex-1 min-w-[240px]`}
          placeholder="Search ref, customer, phone, reg…"
          value={filters.q}
          onChange={(e) => setFilter('q', e.target.value)}
          data-testid="apt-search"
        />
        <select
          className={`${inputCls} max-w-[180px]`}
          value={filters.service_type}
          onChange={(e) => setFilter('service_type', e.target.value)}
          data-testid="apt-filter-service"
        >
          <option value="all">All services</option>
          {(meta.services || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="inline-flex items-center gap-1 text-xs text-slate-600">
          <label className="text-slate-500">From</label>
          <input type="date" className={`${inputCls} py-1.5 text-xs`} value={filters.date_from} onChange={(e) => setFilter('date_from', e.target.value)} data-testid="apt-filter-from" />
          <label className="text-slate-500 ml-1">To</label>
          <input type="date" className={`${inputCls} py-1.5 text-xs`} value={filters.date_to} onChange={(e) => setFilter('date_to', e.target.value)} data-testid="apt-filter-to" />
          {(filters.date_from || filters.date_to) && (
            <button className="text-slate-500 hover:text-rose-600 text-[11px] font-semibold ml-1"
              onClick={() => { setFilter('date_from', ''); setFilter('date_to', ''); }}
              data-testid="apt-filter-clear">Clear</button>
          )}
        </div>
      </div>

      {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{err}</div>}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden sm:overflow-x-auto">
        {loading ? (
          <div className="text-center py-10 text-slate-500"><Loader2 className="inline animate-spin mr-2" size={18} /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm" data-testid="apt-empty">
            <CalendarCheck2 className="mx-auto mb-2 text-slate-300" size={28} />
            No appointments yet for this filter.
          </div>
        ) : (
          <>
            {/* Mobile card list (< sm). Same actions as the desktop table
             *  but stacked vertically so nothing is clipped by the parent
             *  overflow-hidden. Desktop uses the classic <table> below. */}
            <ul className="sm:hidden divide-y divide-slate-100" data-testid="apt-mobile-list">
              {rows.map((r) => {
                const SourceIcon = SOURCE_ICON[r.reference_source] || Globe2;
                const c = r.customer || {}; const v = r.vehicle || {};
                return (
                  <li key={r.id} className="p-3 space-y-2" data-testid={`apt-mobile-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-slate-900">{r.ref_no}</span>
                          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500"><SourceIcon size={10} /> {r.reference_source}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-600 font-semibold tabular-nums">
                          {r.slot_date} · {fmtSlot(r.slot_window)}
                        </div>
                      </div>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <div className="text-slate-400 uppercase text-[9px] font-semibold tracking-wide">Customer</div>
                        <div className="font-semibold text-slate-800 truncate">{c.name || '—'}</div>
                        <div className="text-slate-500 font-mono">{c.phone || '—'}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 uppercase text-[9px] font-semibold tracking-wide">Vehicle</div>
                        <div className="text-slate-800 truncate">{[v.year, v.make, v.model].filter(Boolean).join(' ') || '—'}</div>
                        <div className="text-slate-500 font-mono truncate">{v.registration_number || '—'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">{r.service_type}</span>
                      <ExtraBadges r={r} />
                      {r.advance_amount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                          ₹ {fmtINR(r.advance_amount)} · {r.advance_paid_status}
                        </span>
                      )}
                      {r.assigned_staff_name && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-200" data-testid={`apt-assignee-mobile-${r.id}`}>
                          {r.assigned_staff_name}
                          {r.assigned_staff_id && (
                            <SkillBadge staffId={r.assigned_staff_id} authHeader={authHeader} showCount={false} />
                          )}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 pt-1">
                      {r.status === 'pending' && (
                        <button
                          onClick={() => confirmAndNotify(r)}
                          disabled={busy[r.id]}
                          className="inline-flex items-center gap-1 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white text-[11px] font-semibold px-2 py-1 rounded"
                          data-testid={`apt-m-confirm-${r.id}`}
                        ><CircleCheck size={11} /> Confirm</button>
                      )}
                      {r.status === 'confirmed' && r.customer?.phone && (
                        <button
                          type="button"
                          onClick={() => shareAptCard(r, authHeader)}
                          className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold px-2 py-1 rounded"
                          data-testid={`apt-m-send-card-${r.id}`}
                          title="Send the booking card + confirmation on WhatsApp"
                          ><MessageCircle size={11} /> Send confirmation</button>
                      )}
                      {['pending', 'confirmed'].includes(r.status) && (
                        <button
                          onClick={() => convert(r)}
                          disabled={busy[r.id]}
                          className="inline-flex items-center gap-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white text-[11px] font-semibold px-2 py-1 rounded"
                          data-testid={`apt-m-convert-${r.id}`}
                        ><ClipboardCheck size={11} /> {r.linked_inspection_id ? 'Open' : 'To insp.'}</button>
                      )}
                      {r.status === 'confirmed' && (
                        <button
                          onClick={() => patch(r.id, { status: 'completed' })}
                          disabled={busy[r.id]}
                          className="inline-flex items-center gap-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold px-2 py-1 rounded"
                          data-testid={`apt-m-complete-${r.id}`}
                        >Done</button>
                      )}
                      {['pending', 'confirmed'].includes(r.status) && (
                        <button onClick={() => setRescheduling(r)} className="inline-flex items-center gap-1 border border-sky-300 text-sky-700 hover:bg-sky-50 text-[11px] font-semibold px-2 py-1 rounded" data-testid={`apt-m-reschedule-${r.id}`}>
                          <CalendarClock size={11} /> Reschedule
                        </button>
                      )}
                      {(['pending', 'confirmed'].includes(r.status) || r.linked_job_card_id) && (
                        <button onClick={() => toJobCard(r)} disabled={busy[r.id]} className="inline-flex items-center gap-1 border border-slate-300 text-slate-800 hover:bg-slate-50 text-[11px] font-semibold px-2 py-1 rounded" data-testid={`apt-m-jobcard-${r.id}`}>
                          <Wrench size={11} /> {r.linked_job_card_id ? r.linked_job_card_number : 'Job card'}
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/admin/appointments/${r.id}`)}
                        className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 hover:bg-slate-50 text-[11px] font-semibold px-2 py-1 rounded"
                        data-testid={`apt-m-edit-${r.id}`}
                      >Edit</button>
                      {r.status !== 'cancelled' && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Cancel booking ${r.ref_no}?`)) patch(r.id, { status: 'cancelled' });
                          }}
                          disabled={busy[r.id]}
                          className="inline-flex items-center gap-1 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[11px] font-semibold px-2 py-1 rounded"
                          data-testid={`apt-m-cancel-${r.id}`}
                        ><CircleX size={11} /></button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <table className="hidden sm:table w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Ref</th>
                <th className="px-3 py-2 text-left">Slot</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Vehicle</th>
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-right">Advance</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const SourceIcon = SOURCE_ICON[r.reference_source] || Globe2;
                const c = r.customer || {}; const v = r.vehicle || {};
                return (
                  <tr key={r.id} data-testid={`apt-row-${r.id}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="font-mono text-xs font-semibold text-slate-800">{r.ref_no}</div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1"><SourceIcon size={10} /> {r.reference_source}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-semibold text-slate-800">{r.slot_date}</div>
                      <div className="text-[11px] text-slate-500">{fmtSlot(r.slot_window)}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-semibold text-slate-800 truncate max-w-[160px]">{c.name || '—'}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{c.phone || '—'}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-slate-800">{[v.year, v.make, v.model].filter(Boolean).join(' ') || '—'}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{v.registration_number || '—'}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">{r.service_type}</span>
                      <div className="mt-1 flex flex-wrap items-center gap-1"><ExtraBadges r={r} /></div>
                      {r.assigned_staff_name && (
                        <div className="mt-1 text-[10px] text-teal-700 inline-flex items-center gap-1" data-testid={`apt-assignee-${r.id}`}>
                          <UserCheck size={10} /> {r.assigned_staff_name}
                          {r.assigned_staff_id && (
                            <SkillBadge staffId={r.assigned_staff_id} authHeader={authHeader} showCount={false} />
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-right font-mono">
                      {r.advance_amount > 0 ? (
                        <>
                          <div className="text-emerald-700 font-semibold">{fmtINR(r.advance_amount)}</div>
                          <div className="text-[10px] text-slate-400 uppercase">{r.advance_paid_status}</div>
                        </>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 align-top"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-2 align-top text-right">
                      <div className="inline-flex flex-wrap items-center gap-1 justify-end">
                        {r.status === 'pending' && (
                          <button
                            onClick={() => confirmAndNotify(r)}
                            disabled={busy[r.id]}
                            className="inline-flex items-center gap-1 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white text-[11px] font-semibold px-2 py-1 rounded"
                            data-testid={`apt-confirm-${r.id}`}
                            title="Confirm + open WhatsApp confirmation"
                          ><CircleCheck size={11} /> Confirm</button>
                        )}
                        {r.status === 'confirmed' && r.customer?.phone && (
                          <button
                            type="button"
                            onClick={() => shareAptCard(r, authHeader)}
                            className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold px-2 py-1 rounded"
                            data-testid={`apt-send-card-${r.id}`}
                            title="Send the booking card + confirmation on WhatsApp"
                            ><MessageCircle size={11} /> Send confirmation</button>
                        )}
                        {['pending', 'confirmed'].includes(r.status) && (
                          <button
                            onClick={() => convert(r)}
                            disabled={busy[r.id]}
                            className="inline-flex items-center gap-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white text-[11px] font-semibold px-2 py-1 rounded"
                            data-testid={`apt-convert-${r.id}`}
                            title="Convert to inspection"
                          ><ClipboardCheck size={11} /> {r.linked_inspection_id ? 'Open' : 'To inspection'}</button>
                        )}
                        {r.status === 'confirmed' && (
                          <button
                            onClick={() => patch(r.id, { status: 'completed' })}
                            disabled={busy[r.id]}
                            className="inline-flex items-center gap-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold px-2 py-1 rounded"
                            data-testid={`apt-complete-${r.id}`}
                          >Complete</button>
                        )}
                        {['pending', 'confirmed'].includes(r.status) && (
                          <button onClick={() => setRescheduling(r)} className="inline-flex items-center gap-1 border border-sky-300 text-sky-700 hover:bg-sky-50 text-[11px] font-semibold px-2 py-1 rounded" data-testid={`apt-reschedule-${r.id}`} title="Pick a new slot">
                            <CalendarClock size={11} /> Reschedule
                          </button>
                        )}
                        {(['pending', 'confirmed'].includes(r.status) || r.linked_job_card_id) && (
                          <button onClick={() => toJobCard(r)} disabled={busy[r.id]} className="inline-flex items-center gap-1 border border-slate-300 text-slate-800 hover:bg-slate-50 text-[11px] font-semibold px-2 py-1 rounded" data-testid={`apt-jobcard-${r.id}`} title="Convert to job card">
                            <Wrench size={11} /> {r.linked_job_card_id ? r.linked_job_card_number : 'Job card'}
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/admin/appointments/${r.id}`)}
                          className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 hover:bg-slate-50 text-[11px] font-semibold px-2 py-1 rounded"
                          data-testid={`apt-edit-${r.id}`}
                        >Edit</button>
                        {r.status !== 'cancelled' && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Cancel booking ${r.ref_no}?`)) patch(r.id, { status: 'cancelled' });
                            }}
                            disabled={busy[r.id]}
                            className="inline-flex items-center gap-1 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[11px] font-semibold px-2 py-1 rounded"
                            data-testid={`apt-cancel-${r.id}`}
                          ><CircleX size={11} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
      </div>

      {rescheduling && <RescheduleDialog row={rescheduling} authHeader={authHeader} onClose={() => setRescheduling(null)} onDone={load} />}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div>Page {page} of {totalPages} · {total} total</div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-100 disabled:opacity-40 px-2 py-1 rounded font-semibold"
              data-testid="apt-prev"
            ><ChevronLeft size={12} /> Prev</button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-100 disabled:opacity-40 px-2 py-1 rounded font-semibold"
              data-testid="apt-next"
            >Next <ChevronRight size={12} /></button>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
};

export default AppointmentsPage;
